import {
  useSuspenseQuery,
  useQuery,
  useMutation,
  useQueryClient,
  UseSuspenseQueryOptions,
  UseQueryOptions,
  UseMutationOptions,
} from "@tanstack/react-query";
import { useApiClient } from "../client";
import { creationEntitiesKeys } from "../creation-entities-keys";
import { formatSearchQuery } from "../../utils/format-search-query";
import {
  AdvertisementListPaginatedResponseDto,
  CreateAdvertisementRequestDto,
  AdvertisementResponseDto,
  EditAdvertisementRequestDto,
} from "../interfaces/advertisements.interface";

export interface UseAdvertisementsFindParams {
  textSearch?: string;
  limit?: number;
  page?: number;
}

export const advertisementsKeys = {
  all: ["advertisements"] as const,
  profile: () => [...advertisementsKeys.all, "profile"] as const,
  mutation: () => [...advertisementsKeys.all, "mutation"] as const,
  details: () => [...advertisementsKeys.all, "detail"] as const,
  detail: (id: string) => [...advertisementsKeys.details(), id] as const,
};

export function useAdvertisementsFind(
  params?: UseAdvertisementsFindParams,
  options?: Omit<
    UseSuspenseQueryOptions<AdvertisementListPaginatedResponseDto>,
    "queryKey" | "queryFn"
  >,
) {
  const { apiRequest } = useApiClient();

  return useSuspenseQuery({
    queryKey: [...advertisementsKeys.profile(), ...creationEntitiesKeys.list(), params],
    queryFn: () => {
      const queryString = formatSearchQuery(params);
      return apiRequest<AdvertisementListPaginatedResponseDto>(
        `/sponsors/advertisements${queryString}`,
      );
    },
    staleTime: 0,
    gcTime: 0,
    refetchOnMount: "always",
    ...options,
  });
}

export function useAdvertisementsFindQuery(
  params?: UseAdvertisementsFindParams,
  options?: Omit<
    UseQueryOptions<AdvertisementListPaginatedResponseDto>,
    "queryKey" | "queryFn"
  >,
) {
  const { apiRequest } = useApiClient();

  return useQuery({
    queryKey: [...advertisementsKeys.profile(), ...creationEntitiesKeys.list(), params],
    queryFn: () => {
      const queryString = formatSearchQuery(params);
      return apiRequest<AdvertisementListPaginatedResponseDto>(
        `/sponsors/advertisements${queryString}`,
      );
    },
    staleTime: 0,
    gcTime: 0,
    refetchOnMount: "always",
    ...options,
  });
}

export function useAdvertisementsCreate(
  options?: Omit<
    UseMutationOptions<AdvertisementResponseDto, Error, CreateAdvertisementRequestDto>,
    "mutationFn"
  >,
) {
  const { apiRequest } = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    ...options,
    mutationFn: (data: CreateAdvertisementRequestDto) =>
      apiRequest<AdvertisementResponseDto>(`/sponsors/advertisements`, "POST", data),
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries({ queryKey: advertisementsKeys.all });
      queryClient.invalidateQueries({
        predicate: (query) => {
          const queryKey = query.queryKey as string[];
          return queryKey.includes("creation-entities");
        },
      });
      options?.onSuccess?.(data, variables, context);
    },
  });
}

export function useAdvertisementsDelete(
  options?: Omit<UseMutationOptions<any, Error, { id: string }>, "mutationFn">,
) {
  const { apiRequest } = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    ...options,
    mutationFn: ({ id }: { id: string }) =>
      apiRequest<any>(`/sponsors/advertisements/${id}`, "DELETE"),
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries({ queryKey: advertisementsKeys.all });
      queryClient.invalidateQueries({
        predicate: (query) => {
          const queryKey = query.queryKey as string[];
          return queryKey.includes("creation-entities");
        },
      });
      options?.onSuccess?.(data, variables, context);
    },
  });
}

export function useAdvertisementsBulkDelete(
  options?: Omit<UseMutationOptions<any, Error, { ids: string[] }>, "mutationFn">,
) {
  const { apiRequest } = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    ...options,
    mutationFn: ({ ids }: { ids: string[] }) =>
      apiRequest<any>(`/sponsors/advertisements/bulk-delete`, "POST", { ids }),
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries({ queryKey: advertisementsKeys.all });
      queryClient.invalidateQueries({
        predicate: (query) => {
          const queryKey = query.queryKey as string[];
          return queryKey.includes("creation-entities");
        },
      });
      options?.onSuccess?.(data, variables, context);
    },
  });
}

export function useAdvertisementsUpdate(
  options?: Omit<
    UseMutationOptions<AdvertisementResponseDto, Error, { id: string; data: EditAdvertisementRequestDto }>,
    "mutationFn"
  >,
) {
  const { apiRequest } = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    ...options,
    mutationFn: ({ id, data }) =>
      apiRequest<AdvertisementResponseDto>(`/sponsors/advertisements/${id}`, "PATCH", data),
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries({ queryKey: advertisementsKeys.all });
      queryClient.invalidateQueries({
        predicate: (query) => {
          const queryKey = query.queryKey as string[];
          return queryKey.includes("creation-entities");
        },
      });
      options?.onSuccess?.(data, variables, context);
    },
  });
}
